import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useCurrentProgram } from "@/hooks/useCurrentProgram";
import { usePlanningWeek } from "@/hooks/usePlanningWeek";
import { useCyclePlanning } from "@/hooks/useCyclePlanning";
import { useWeeklyPlanOverlay } from "@/hooks/useWeeklyPlanOverlay";
import { useScheduleMap } from "@/hooks/useScheduleMap";
import { useDailyProgression } from "@/hooks/useDailyProgression";
import { useDynamicFatigue } from "@/hooks/useDynamicFatigue";
import type { PlanningBlock } from "@/types/planning";
import type { WeekType } from "@/types/weekTypes";
import type { StatusIndicatorProps } from "@/components/dashboard/DashboardStatusIndicator";

const FREE_MODE_KEY = "slot-proposals-free-mode";

export type PageContext = "dashboard" | "planning" | "programs" | "progression" | "prevention" | "profile" | "other";

/** Pages that already mount the same heavy hooks — skip to avoid duplicate DB calls */
const HEAVY_PAGES = ["/dashboard", "/programs", "/sessions", "/planning"];

function isHeavyPage(pathname: string): boolean {
  return HEAVY_PAGES.some((p) => pathname.startsWith(p));
}

function routeToPageContext(pathname: string): PageContext {
  if (pathname.startsWith("/dashboard")) return "dashboard";
  if (pathname.startsWith("/programs")) return "programs";
  if (pathname.startsWith("/prevention")) return "prevention";
  if (pathname.startsWith("/profile") || pathname.startsWith("/onboarding")) return "profile";
  return "other";
}

const EMPTY_PROPS: StatusIndicatorProps = {
  hasPlanning: false,
  programTitle: null,
  weekType: null,
  freeMode: false,
  pendingProposals: 0,
  hasChaos: false,
  fatigueScore: null,
  isOverride: false,
  fatigueFactors: [],
  fatigueReliable: false,
  consecutiveTrainingDays: 0,
  missedSessionDays: 0,
  recentPainDays: 0,
  activeTab: "other" as any,
};

/**
 * Global status hook — provides all data needed by DashboardStatusIndicator.
 * Designed to run in AppLayout so the indicator is visible on every page.
 *
 * On heavy pages (dashboard, programs, sessions, planning) it skips its own
 * fetches to avoid duplicating queries that those pages already execute.
 * The status bar is hidden on those pages anyway (GlobalStatusBar returns null).
 */
export function useGlobalStatus(): {
  props: StatusIndicatorProps;
  loading: boolean;
} {
  const location = useLocation();
  const pageContext = routeToPageContext(location.pathname);
  const skip = isHeavyPage(location.pathname);

  // All hooks must be called unconditionally (Rules of Hooks).
  // Pass skip hint so hooks can avoid actual fetching when skipped.
  const { currentProgram, currentProgramId } = useCurrentProgram();
  const legacyPlanning = usePlanningWeek();
  const cyclePlanning = useCyclePlanning();

  const isCycleMode = cyclePlanning.cycle?.enabled ?? false;

  const currentWeekData = useMemo(() => {
    if (skip) return null;
    if (!isCycleMode) return null;
    const weeks = cyclePlanning.computeWeeks(1);
    return weeks[0] ?? null;
  }, [skip, isCycleMode, cyclePlanning]);

  const activeBlocks: PlanningBlock[] = useMemo(() => {
    if (skip) return [];
    if (isCycleMode && currentWeekData) {
      return (currentWeekData.blocks || []).map((b: any) => ({
        ...b,
        id: b.id || crypto.randomUUID(),
      }));
    }
    return legacyPlanning.blocks;
  }, [skip, isCycleMode, currentWeekData, legacyPlanning.blocks]);

  const { fatigue } = useDynamicFatigue();

  const sleepTarget = legacyPlanning.rules?.sleep_target;
  const { mappings: scheduleMappings } = useScheduleMap(skip ? null : currentProgramId);
  const { overlayBlocks, programTitle: overlayProgramTitle, loading: overlayLoading } =
    useWeeklyPlanOverlay(
      skip ? null : currentProgramId,
      skip ? [] : activeBlocks,
      sleepTarget,
      undefined,
      skip ? [] : scheduleMappings,
    );

  const { days, loading: progressionLoading } = useDailyProgression(
    skip ? [] : overlayBlocks,
    skip ? null : currentProgramId,
  );

  const freeMode = !skip && typeof window !== "undefined"
    ? localStorage.getItem(FREE_MODE_KEY) === "true"
    : false;

  const weekType = useMemo((): WeekType | null => {
    if (skip) return null;
    if (isCycleMode && currentWeekData) return currentWeekData.weekType as WeekType;
    return null;
  }, [skip, isCycleMode, currentWeekData]);

  const consecutiveTrainingDays = useMemo(() => {
    if (skip) return 0;
    let streak = 0;
    const sorted = [...days].sort((a, b) => b.date.localeCompare(a.date));
    for (const d of sorted) {
      if (d.checkin?.training_done) streak++;
      else break;
    }
    return streak;
  }, [skip, days]);

  const missedSessionDays = useMemo(() => {
    if (skip) return 0;
    return days.filter(
      (d) => d.isPast && d.plannedSession && !d.checkin?.training_done
    ).length;
  }, [skip, days]);

  const recentPainDays = useMemo(() => {
    if (skip) return 0;
    return days
      .slice(-3)
      .filter((d) => d.checkin?.pain_flags && d.checkin.pain_flags.length > 0).length;
  }, [skip, days]);

  // On heavy pages, return empty props immediately (after all hooks)
  if (skip) {
    return { props: { ...EMPTY_PROPS, activeTab: pageContext as any }, loading: false };
  }

  const programTitle = currentProgram?.title || overlayProgramTitle || null;
  const isOverride = currentWeekData?.isOverride ?? false;
  const hasChaos = isOverride || (fatigue?.score ?? 0) >= 7;

  const planningLoading = isCycleMode ? cyclePlanning.loading : legacyPlanning.loading;
  const loading = planningLoading || progressionLoading || overlayLoading;

  const props: StatusIndicatorProps = {
    hasPlanning: activeBlocks.length > 0,
    programTitle,
    weekType,
    freeMode,
    pendingProposals: 0,
    hasChaos,
    fatigueScore: fatigue?.score ?? null,
    isOverride,
    fatigueFactors: fatigue?.factors ?? [],
    fatigueReliable: fatigue?.reliable ?? false,
    consecutiveTrainingDays,
    missedSessionDays,
    recentPainDays,
    activeTab: pageContext as any,
  };

  return { props, loading };
}
